import { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./signaturePad.module.css";

function SignaturePad({
  onConfirm,
  disabled,
}: {
  onConfirm: (signatureDataUrl: string) => void;
  disabled?: boolean;
}) {
  const signatureRef = useRef<SignatureCanvas>(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  const handleClear = () => {
    signatureRef.current?.clear();
    setHasDrawn(false);
  };

  const handleEnd = () => {
    setHasDrawn(!signatureRef.current?.isEmpty());
  };

  const handleConfirm = () => {
    const canvas = signatureRef.current;
    if (!canvas || canvas.isEmpty()) return;
    const dataUrl = canvas.toDataURL("image/png");
    // Only ever emit a PNG data-URL — guards against non-image payloads downstream.
    if (!dataUrl.startsWith("data:image/png;base64,")) return;
    onConfirm(dataUrl);
  };

  return (
    <div className={styles.signaturePad}>
      <div className={styles.signaturePad__canvasWrapper}>
        <SignatureCanvas
          ref={signatureRef}
          canvasProps={{ className: styles.signaturePad__canvas }}
          penColor="#1f2937"
          backgroundColor="white"
          clearOnResize={false}
          onEnd={handleEnd}
          // Smoother, rounder ink: higher minWidth avoids sharp pointed ends,
          // narrower min/max keeps the stroke flexible and even.
          minWidth={1.3}
          maxWidth={2.6}
          velocityFilterWeight={0.5}
          dotSize={1.8}
        />
        <p
          className={`${styles.signaturePad__hint} ${
            hasDrawn ? styles.signaturePad__hint_hidden : ""
          }`}
        >
          {t("SIGNATURE_PAD_HINT")}
        </p>
      </div>
      <div className={styles.signaturePad__buttons}>
        <button
          type="button"
          className={styles.signaturePad__clearBtn}
          onClick={handleClear}
          disabled={disabled}
        >
          {t("SIGNATURE_PAD_CLEAR")}
        </button>
        <button
          type="button"
          className={styles.signaturePad__confirmBtn}
          onClick={handleConfirm}
          disabled={disabled || !hasDrawn}
          data-testid="signature-confirm"
        >
          {t("SIGNATURE_PAD_CONFIRM")}
        </button>
      </div>
    </div>
  );
}

export default SignaturePad;
